import type { CSSProperties, ReactNode } from 'react';
import type { PostureStatus } from '@/components/ui/posture-chip';
import type { Recommendation } from '@/lib/api/health-readiness';
import { cn } from '@/lib/utils/cn';

/**
 * The vocabulary the chart primitives share (T-0241, E22 design §3): the
 * posture and severity words, the gate zones, the chart-sized empty state,
 * the visually hidden table every chart ships, and the legend swatches.
 *
 * Integrity rule: a chart positions marks by date or value and may say
 * which gate zone a value falls in (a comparison against the thresholds it
 * is given). It never adds anything up: totals, counts, verdicts, slips and
 * projections arrive in props, computed by the API.
 *
 * Visual contract: docs/health-readiness/prototype/health-readiness-v2.{js,css}.
 */

export interface Thresholds {
  go: number;
  conditional: number;
}

/** The prototype's posture words: posture colour always travels with one. */
export const POSTURE_LABEL: Record<PostureStatus, string> = {
  healthy: 'Green',
  'at-risk': 'Amber',
  critical: 'Red',
  unassessed: 'Not rated',
};

/** Worst first: the unit bar's order and the legend's. */
export const POSTURE_WORST_FIRST: PostureStatus[] = [
  'critical',
  'at-risk',
  'healthy',
  'unassessed',
];

/** Solid fills for SVG marks. */
export const POSTURE_FILL: Record<PostureStatus, string> = {
  healthy: 'var(--posture-healthy)',
  'at-risk': 'var(--posture-at-risk)',
  critical: 'var(--posture-critical)',
  unassessed: 'var(--ink-100)',
};

/** Solid fills for HTML marks; not rated is hatched. */
export const POSTURE_BG: Record<PostureStatus, string> = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
  unassessed: 'chart-unrated',
};

/** Gate-zone tints (No-Go, Conditional, Go) as backgrounds. */
export const ZONE_TINT_BG: Record<PostureStatus, string> = {
  healthy: 'bg-posture-healthy-tint',
  'at-risk': 'bg-posture-at-risk-tint',
  critical: 'bg-posture-critical-tint',
  unassessed: 'bg-ink-100',
};

/** What a score's zone is called next to its colour. */
export const ZONE_LABEL: Record<PostureStatus, string> = {
  healthy: 'At Go',
  'at-risk': 'Conditional',
  critical: 'Below Conditional',
  unassessed: 'Not scored',
};

/**
 * Which gate zone a 0..100 value falls in. A comparison against the
 * thresholds passed in; the value and the thresholds both come from the API.
 */
export function zoneOf(value: number | null | undefined, thresholds: Thresholds): PostureStatus {
  if (value === null || value === undefined) return 'unassessed';
  if (value >= thresholds.go) return 'healthy';
  if (value >= thresholds.conditional) return 'at-risk';
  return 'critical';
}

export const VERDICT_LABEL: Record<Recommendation, string> = {
  go: 'Go',
  conditional_go: 'Conditional',
  no_go: 'No-Go',
};

export const VERDICT_POSTURE: Record<Recommendation, PostureStatus> = {
  go: 'healthy',
  conditional_go: 'at-risk',
  no_go: 'critical',
};

export type Severity = 'critical' | 'high' | 'medium' | 'low';
export type SeverityCounts = Record<Severity, number>;

export const SEVERITY_ORDER: Severity[] = ['critical', 'high', 'medium', 'low'];

export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const SEVERITY_BG: Record<Severity, string> = {
  critical: 'bg-severity-critical',
  high: 'bg-severity-high',
  medium: 'bg-severity-medium',
  low: 'bg-severity-low',
};

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "$4.43M", "$4.4M", "$180k": the prototype's money(). */
export function formatMoney(amount: number): string {
  if (Math.abs(amount) >= 1e6) {
    return `$${(amount / 1e6).toFixed(2).replace(/0$/, '')}M`;
  }
  return `$${Math.round(amount / 1000)}k`;
}

/** Percent as the charts print it: whole numbers. */
export function formatPct(value: number): string {
  return `${Math.round(value)}%`;
}

/** CSS custom properties on a style prop (the stagger index `--i`, etc.). */
export function cssVars(vars: Record<string, string | number>): CSSProperties {
  return vars as CSSProperties;
}

/** Fixed-precision SVG coordinates keep the markup small and stable. */
export const f1 = (n: number) => Math.round(n * 10) / 10;

/**
 * The prototype's `.empty` box at chart size: dashed hairline, warm paper,
 * a bold line and a plain one.
 */
export function ChartEmpty({
  title,
  children,
  className,
}: {
  title: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-card border border-dashed border-ink-200 bg-paper-50 px-4 py-7 text-center text-[13px] leading-5 text-ink-500',
        className,
      )}
    >
      <strong className="mb-1 block text-[14px] font-semibold text-ink-900">{title}</strong>
      {children}
    </div>
  );
}

/**
 * The table view every chart ships: visually hidden, read by screen
 * readers, the same numbers the marks draw. Cells are text only.
 */
export function SrTable({
  caption,
  head,
  rows,
}: {
  caption: string;
  head: string[];
  rows: Array<Array<string | number>>;
}) {
  return (
    <table className="sr-only" data-chart-table="">
      <caption>{caption}</caption>
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h} scope="col">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) =>
              j === 0 ? (
                <th key={j} scope="row">
                  {cell}
                </th>
              ) : (
                <td key={j}>{cell}</td>
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export type SwatchKind = 'box' | 'line' | 'dash' | 'diamond' | 'diamond-hollow';

/** One legend key: a swatch (a token class for its colour) and its words. */
export interface LegendItem {
  label: ReactNode;
  swatch?: SwatchKind;
  /** Background (or border, for `dash` and `diamond-hollow`) token class. */
  color: string;
}

function Swatch({ kind = 'box', color }: { kind?: SwatchKind; color: string }) {
  const base = 'inline-block flex-none';
  switch (kind) {
    case 'line':
      return <span aria-hidden className={cn(base, 'h-0.5 w-3.5 rounded-[1px]', color)} />;
    case 'dash':
      return <span aria-hidden className={cn(base, 'h-0 w-3.5 border-t-2 border-dashed', color)} />;
    case 'diamond':
      return (
        <span aria-hidden className={cn(base, 'h-[9px] w-[9px] rotate-45 rounded-[2px]', color)} />
      );
    case 'diamond-hollow':
      return (
        <span
          aria-hidden
          className={cn(base, 'h-[9px] w-[9px] rotate-45 rounded-[2px] border-2 bg-paper-0', color)}
        />
      );
    default:
      return <span aria-hidden className={cn(base, 'h-2.5 w-2.5 rounded-[3px]', color)} />;
  }
}

/**
 * The prototype's `.legend` row: 12px ink-700 keys, bold counts, and an
 * optional trailing slot (a link) pushed to the right.
 */
export function ChartLegend({
  items,
  children,
  className,
}: {
  items: LegendItem[];
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3.5 gap-y-1 text-meta text-ink-700',
        className,
      )}
    >
      {items.map((item, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          <Swatch kind={item.swatch} color={item.color} />
          {item.label}
        </span>
      ))}
      {children ? <span className="ml-auto">{children}</span> : null}
    </div>
  );
}
